import addFit from 'widget/utils/helpers/addFit';
import Analytics from 'modules/Analytics/Analytics';
import BigThumbnail from 'widget/components/BigThumbnail';
import END_REASON from 'modules/Analytics/constants/endReason';
import ExternalLink from 'widget/components/ExternalLink';
import InfoButton from 'widget/components/InfoButton';
import InfoCard from 'widget/components/InfoCard';
import LeftButtonGroup from 'widget/components/LeftButtonGroup';
import LeftButtonGroupControls from 'widget/components/LeftButtonGroupControls';
import mediaQueries from 'styles/mediaQueries';
import PlaybackError from 'widget/Errors/PlaybackError';
import PlayButton from 'widget/components/PlayButton';
import PlayButtonWrapper from 'widget/components/PlayButtonWrapper';
import Player from 'widget/contexts/Player';
import player from 'widget/player';
import PlayerContainer from 'widget/components/PlayerContainer';
import PlayerState, {
  PlayerErrorState,
} from 'modules/Player/constants/playerState';
import PlayNextButton from 'widget/components/PlayNextButton';
import Progress from 'widget/components/Progress';
import RightButtonGroup from 'widget/components/RightButtonGroup';
import ShareButton from 'widget/components/ShareButton';
import ShareCard from 'widget/components/ShareCard';
import ShouldShow from 'components/ShouldShow';
import Toolbar from 'widget/components/Toolbar';
import unescapeHTMLEntities from 'decode-html';
import useModal from 'widget/components/Modal/hooks/useModal';
import useMount from 'hooks/useMount';
import useTrackers from 'widget/contexts/Trackers/hooks/useTrackers';
import withPlayerJS from 'widget/components/withPlayerJS';
import { Events } from 'widget/trackers/types';
import {
  generatePlaylistRadioTrackingData,
  getPlaylistRadioAnalyticsPayload,
} from 'widget/PlaylistRadio/helpers/analytics';
import { getErrorMessageTranslator } from 'widget/Errors/PlaybackError/helpers/getErrorMessageTranslator';
import { PlayerJSRefs } from 'widget/components/withPlayerJS/types';
import { SyntheticEvent, useContext, useEffect, useRef, useState } from 'react';
import { usePlayerProgress, useTranslate } from 'widget/hooks';
import { WIDGET_DIMENSIONS } from 'constants/widgetDimensions';
import { withRetries } from 'widget/utils/helpers/withRetries';

function PlaylistRadioPlayer({
  analytics,
  analyticsData,
  autoplay,
  description,
  embedUrl,
  followUrl,
  hasPlayed,
  imageUrl,
  isError,
  reportingKey,
  playbackSupportedCountry,
  playlistRadioUrl,
  positionBottom,
  refs,
  shareText,
  shareTitle,
  title,
  trackIds,
}: {
  analytics?: Analytics;
  analyticsData: {
    name: string;
    subName: string;
    subId: number;
    songName: string;
    playedFrom: number;
  };
  autoplay?: boolean;
  description?: string;
  embedUrl: string;
  followUrl: string;
  hasPlayed: boolean;
  imageUrl?: string;
  isError?: boolean;
  playbackSupportedCountry?: boolean;
  playlistRadioUrl: string;
  positionBottom: boolean;
  refs?: PlayerJSRefs;
  reportingKey: string;
  shareText: string;
  shareTitle: string;
  title: string;
  trackIds: Array<number>;
}) {
  const trackers = useTrackers();
  const {
    play: onClickPlay,
    playerState,
    playNextPlaylistRadio,
  } = useContext(Player.Context);
  const [playClicked, setPlayClicked] = useState(false);
  const [isAA, setIsAA] = useState(false);
  const [isPlaybackError, setIsPlaybackError] = useState<boolean>(false);
  const [streamInitTime, setStreamInitTime] = useState<number>(0);
  const trackingDataRef = useRef(
    generatePlaylistRadioTrackingData({ ...analyticsData, reportingKey }),
  );
  const playedFromRef = useRef(analyticsData.playedFrom);
  const [progress] = usePlayerProgress();
  const isPlaying = playerState === PlayerState.Playing;
  const isBuffering = playerState === PlayerState.Buffering;
  const isLoading = playerState === PlayerState.Loading;
  const translate = useTranslate();
  const getPlaybackErrorMessage = getErrorMessageTranslator(translate);

  const trackerData = {
    name: analyticsData.name,
    type: 'playlist',
    typeId: reportingKey,
  };

  const [InfoModal, toggleInfoModal] = useModal(
    <InfoCard
      body={description ? unescapeHTMLEntities(description) : ''}
      title={title}
      url={playlistRadioUrl}
    />,
  );

  const [ShareModal, toggleShareModal] = useModal(
    <ShareCard
      analytics={analytics}
      analyticsData={{
        id: reportingKey,
        name: trackerData.name,
        type: trackerData.type,
      }}
      dimensions={WIDGET_DIMENSIONS.PLAYLIST}
      followUrl={followUrl}
      shareText={shareText}
      shareTitle={shareTitle}
      url={playlistRadioUrl}
      urlWithQuery={embedUrl}
    />,
  );

  const [UnsupportedModal, toggleUnsupportedModal] = useModal(
    <InfoCard
      body={unescapeHTMLEntities(
        `<p>
          ${translate('This playlist is unsupported in your current country.')}
        </p>`,
      )}
      title={translate('Playback Unsupported In Current Country')}
      url=""
    />,
  );

  const play = (
    _e?: SyntheticEvent,
    playedFrom: number = analyticsData.playedFrom,
  ) => {
    if (playbackSupportedCountry) {
      if (playedFrom !== playedFromRef.current) {
        playedFromRef.current = playedFrom;
        trackingDataRef.current = generatePlaylistRadioTrackingData({
          ...analyticsData,
          playedFrom,
          reportingKey,
        });
      }

      if (isPlaying) {
        trackers.track(Events.Pause);
        analytics?.track(Events.Pause, trackingDataRef.current);
        analytics?.track(
          Events.StreamEnd,
          getPlaylistRadioAnalyticsPayload(trackingDataRef.current, {
            endReason: END_REASON.PAUSE,
            listenTime: Math.round(progress?.position ?? 0),
          }),
        );
      } else {
        analytics?.track(Events.Play, trackingDataRef.current);
        setStreamInitTime(Date.now());
        trackers.track(Events.Play, trackerData);
      }

      setPlayClicked(true);
      onClickPlay(reportingKey, { trackIds });
    } else {
      toggleUnsupportedModal();
    }
  };

  /**
   * IHRWEB-14939 methods mute, setCurrentTime, setLoop, setVolume, & unmute
   * are currently only externally triggered via player.js; react event
   * parameter included for methods with args for possible widget UI.
   * analytics tbd in IHRWEB-14917.
   */
  const mute = () => {
    player.mute();
    trackers.track(Events.Mute);
  };

  const setLoop = (_e: null | SyntheticEvent, shouldLoop: string | boolean) => {
    if (
      typeof shouldLoop === 'boolean' ||
      shouldLoop === 'true' ||
      shouldLoop === 'false'
    ) {
      player.setLoop(shouldLoop && shouldLoop !== 'false');
    }
  };

  const setVolume = (_e: null | SyntheticEvent, volume: string | number) => {
    player.setVolume(parseInt(volume as string, 10));
  };

  const unmute = () => {
    player.unmute();
    trackers.track(Events.Unmute, { isPlaying });
  };

  if (refs) {
    /* eslint-disable no-param-reassign */
    refs.mute.current = mute;
    refs.pause.current = play; // pause maps to toggling play method
    refs.play.current = play;
    refs.setLoop.current = setLoop;
    refs.setVolume.current = setVolume;
    refs.unmute.current = unmute;
    /* eslint-enable no-param-reassign */
  }

  useMount(() => {
    setIsAA(
      window?.analyticsData?.global?.user?.subscriptionTier === 'PREMIUM',
    );
    if (autoplay) play();
  });

  const playNextPlaylistRadioWithRetries = withRetries(
    () => playNextPlaylistRadio(reportingKey, false, trackIds),
    () => setIsPlaybackError(true),
  );

  useEffect(() => {
    if (playerState === PlayerState.Complete) {
      playNextPlaylistRadioWithRetries();
    }
  }, [playerState]);

  useEffect(() => {
    if (playerState === PlayerState.Playing) {
      setIsPlaybackError(false);
      if (streamInitTime) {
        analytics?.track(
          Events.StreamStart,
          getPlaylistRadioAnalyticsPayload(trackingDataRef.current, {
            playbackStartTime: Date.now(),
            streamInitTime,
          }),
        );
        setStreamInitTime(0);
        trackers.track(Events.StreamStart, trackerData);
      }
    }
  }, [playerState, streamInitTime]);

  useEffect(() => {
    if (
      isError ||
      Object.values(PlayerErrorState).includes(playerState as PlayerErrorState)
    ) {
      const networkError = [
        PlayerErrorState.AccessDenied,
        PlayerErrorState.NetworkError,
      ].includes(playerState as PlayerErrorState);
      analytics?.track(
        Events.PlaybackError,
        getPlaylistRadioAnalyticsPayload(trackingDataRef.current, {
          errorMessage: getPlaybackErrorMessage(
            playerState as PlayerErrorState,
          ),
          errorType: networkError ? 'network_error' : 'playback_failure',
        }),
      );
    }
  }, [isError, playerState]);

  if (isError && isPlaybackError) {
    return <PlaybackError error={playerState as PlayerErrorState} />;
  }

  return (
    <div>
      <PlayerContainer positionBottom={positionBottom}>
        <Progress scrubDisabled={!isAA} supportMobileView={false} />
        <Toolbar>
          <LeftButtonGroup>
            <PlayButtonWrapper data-test="play-button-wrapper">
              <PlayButton
                isLoading={isBuffering || isLoading}
                isPlaying={isPlaying || isBuffering || isLoading}
                onClick={play}
              />
            </PlayButtonWrapper>
            <LeftButtonGroupControls>
              <ShouldShow shouldShow={hasPlayed || playClicked}>
                <PlayNextButton
                  onClick={() => {
                    trackingDataRef.current = generatePlaylistRadioTrackingData(
                      {
                        ...analyticsData,
                        reportingKey,
                      },
                    );
                    analytics?.track(Events.Skip, trackingDataRef.current);
                    playNextPlaylistRadioWithRetries();
                  }}
                  sizeToHide={mediaQueries.max.width['149']}
                />
              </ShouldShow>
            </LeftButtonGroupControls>
          </LeftButtonGroup>
          <RightButtonGroup>
            <InfoButton
              onClick={toggleInfoModal}
              sizeToHide={mediaQueries.max.width['239']}
            />
            <ShareButton
              onClick={toggleShareModal}
              sizeToHide={mediaQueries.max.width['199']}
            />
            {imageUrl && (
              <ExternalLink data-test="thumbnail-link" href={playlistRadioUrl}>
                <BigThumbnail alt={title} src={addFit(imageUrl)} />
              </ExternalLink>
            )}
          </RightButtonGroup>
        </Toolbar>
      </PlayerContainer>
      {ShareModal}
      {InfoModal}
      {UnsupportedModal}
    </div>
  );
}

export default withPlayerJS(PlaylistRadioPlayer);
